import { Card, CardContent } from "../ui/card";
import { Car, Sparkles, Wallet, CreditCard, Landmark } from "lucide-react";
import { StudioSection } from "./StudioSection";

/**
 * Közös KPI bento: hero bevétel + 5 csempe (Mai / Havi) — Főoldal és Statisztika.
 */
export function StudioKpiBento({
  stats,
  idPrefix = "kpi",
  todayTitle = "Mai mutatók",
  todaySubtitle = "Gyors áttekintés — bevétel + bontás",
  monthTitle = "Havi összesítő",
  monthSubtitle = "Hónapban eddig — bontás fizetési mód szerint",
}) {
  if (!stats) return null;

  return (
    <div className="studio-dash-bento space-y-8">
      <StudioSection id={`${idPrefix}-today`} title={todayTitle} subtitle={todaySubtitle}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-stretch">
          <Card className="glass-card kpi-card studio-kpi-hero lg:col-span-4 flex flex-col">
            <CardContent className="p-5 sm:p-6 flex flex-col flex-1 justify-between min-h-[168px]">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Mai bevétel</p>
              <p className="text-3xl sm:text-4xl font-bold text-white tabular-nums leading-tight mt-3">
                {(stats.today_revenue || 0).toLocaleString("hu-HU")}
                <span className="text-lg sm:text-xl font-semibold text-slate-500 ml-1.5">Ft</span>
              </p>
              <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-400 border-t border-slate-800/80 pt-3">
                <span className="tabular-nums">{stats.today_cars} autó</span>
                <span className="text-slate-600 hidden sm:inline">·</span>
                <span className="tabular-nums">{stats.today_services || 0} szolgáltatás</span>
              </div>
            </CardContent>
          </Card>
          <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Tile label="Mai autók" value={stats.today_cars} icon={Car} tone="green" numeric />
            <Tile label="Mai szolgáltatás" value={stats.today_services || 0} icon={Sparkles} tone="cyan" numeric />
            <Tile
              label="Készpénz"
              value={`${(stats.today_cash || 0).toLocaleString("hu-HU")}`}
              suffix=" Ft"
              icon={Wallet}
              tone="green"
            />
            <Tile
              label="Kártya"
              value={`${(stats.today_card || 0).toLocaleString("hu-HU")}`}
              suffix=" Ft"
              icon={CreditCard}
              tone="blue"
            />
            <Tile
              label="Átutalás"
              value={`${(stats.today_transfer || 0).toLocaleString("hu-HU")}`}
              suffix=" Ft"
              icon={Landmark}
              tone="purple"
              wide
            />
          </div>
        </div>
      </StudioSection>

      <StudioSection id={`${idPrefix}-month`} title={monthTitle} subtitle={monthSubtitle}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-stretch">
          <Card className="glass-card kpi-card studio-kpi-hero lg:col-span-4 flex flex-col">
            <CardContent className="p-5 sm:p-6 flex flex-col flex-1 justify-between min-h-[168px]">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Havi bevétel (eddig)</p>
              <p className="text-3xl sm:text-4xl font-bold text-white tabular-nums leading-tight mt-3">
                {(stats.month_revenue || 0).toLocaleString("hu-HU")}
                <span className="text-lg sm:text-xl font-semibold text-slate-500 ml-1.5">Ft</span>
              </p>
              <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-400 border-t border-slate-800/80 pt-3">
                <span className="tabular-nums">{stats.month_cars} autó</span>
                <span className="text-slate-600 hidden sm:inline">·</span>
                <span className="tabular-nums">{stats.month_services || 0} szolgáltatás</span>
              </div>
            </CardContent>
          </Card>
          <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Tile label="Havi autók" value={stats.month_cars} icon={Car} tone="green" numeric />
            <Tile label="Havi szolgáltatás" value={stats.month_services || 0} icon={Sparkles} tone="cyan" numeric />
            <Tile
              label="Havi készpénz"
              value={`${(stats.month_cash || 0).toLocaleString("hu-HU")}`}
              suffix=" Ft"
              icon={Wallet}
              tone="green"
            />
            <Tile
              label="Havi kártya"
              value={`${(stats.month_card || 0).toLocaleString("hu-HU")}`}
              suffix=" Ft"
              icon={CreditCard}
              tone="blue"
            />
            <Tile
              label="Havi átutalás"
              value={`${(stats.month_transfer || 0).toLocaleString("hu-HU")}`}
              suffix=" Ft"
              icon={Landmark}
              tone="purple"
              wide
            />
          </div>
        </div>
      </StudioSection>
    </div>
  );
}

function Tile({ label, value, suffix = "", icon: Icon, tone, numeric, wide }) {
  const toneBg = {
    green: "bg-green-500/15",
    cyan: "bg-cyan-500/15",
    blue: "bg-blue-500/15",
    purple: "bg-purple-500/15",
  };
  const toneText = {
    green: "text-green-400",
    cyan: "text-cyan-400",
    blue: "text-blue-400",
    purple: "text-purple-300",
  };
  return (
    <Card className={`glass-card kpi-card studio-kpi-tile ${wide ? "sm:col-span-2 lg:col-span-1" : ""}`}>
      <CardContent className="p-3 sm:p-4 h-full flex flex-col justify-between">
        <p className="text-[10px] sm:text-xs text-slate-500 uppercase tracking-wide">{label}</p>
        <div className="flex items-end justify-between gap-2 mt-2">
          {numeric ? (
            <p className="text-xl sm:text-2xl font-bold text-white tabular-nums">{value}</p>
          ) : (
            <p className={`text-lg sm:text-xl font-bold tabular-nums leading-snug ${toneText[tone]}`}>
              {value}
              {suffix ? <span className="text-xs font-normal text-slate-500">{suffix}</span> : null}
            </p>
          )}
          <div className={`w-9 h-9 rounded-xl ${toneBg[tone]} flex items-center justify-center shrink-0`}>
            <Icon className={`w-5 h-5 ${toneText[tone]}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
